import {ComponentRef, Inject, Injectable, OnDestroy} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {
    AbstractSearchModel,
    AuthService,
    ConfigurationService,
    DataResourceModel,
    LoginDetails,
    MODULE_VERSIONS,
    SearchModel
} from "@fedeltapointofsale/data-client";
import {Config, Local} from "../config";
import {FUIModalsService, FUIStackedPageModalComponent} from "@fedeltapointofsale/ui";
import {CustomerSupportMessagingService} from "../customer-support-messaging/customer-support-messaging.service";
import {AnalyticEvent, AnalyticType} from "./analytic-event";

declare let gtag: any;

/**
 * Service to send events to Google Analytics
 */
@Injectable()
export class AnalyticsService implements OnDestroy {

    private static readonly GA_TRACKING_ID: string = "UA-97316752-6";
    private static disabled: boolean = true;

    /**
     * WBO client Id. Should be the same name as the sub-domain. This is used to identify the company
     */
    private clientId: string;

    private wboVersion: string;

    private subscriptions: Subscription = new Subscription();

    private currentPage: string;


    constructor(private router: Router,
                private authService: AuthService,
                private configurationService: ConfigurationService,
                private modalService: FUIModalsService,
                private customerSupportMessageService: CustomerSupportMessagingService,
                @Inject(MODULE_VERSIONS) private moduleVersions: any[]) {

        if (AnalyticsService.disabled) {
            return;
        }

        if (moduleVersions) {
            moduleVersions.forEach((v) => {
                if (v.module === "wbo") {
                    console.log("version: " + v.version);
                    this.wboVersion = v.version;
                }
            });
        }

        this.subscriptions.add(this.authService.loginDetails$.subscribe((l: LoginDetails) => {
            if (l.user && !this.clientId) {
                setTimeout(() => this.initClientId());
            }
        }));

        this.subscriptions.add(this.router.events.subscribe((e) => {
            if (e instanceof NavigationEnd) {
                const split: string[] = e.urlAfterRedirects.split("/");

                let url: string;
                if (split.length > 2) {
                    url = "/" + split[1] + "/" + split[2];
                } else {
                    url = e.urlAfterRedirects;
                }
                this.trackRoute(url);
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public init(): void {
        this.initClientId();
    }

    private initClientId(): void {
        this.configurationService.getString(Config.CLIENT_ID).then((id: string) => {
            if (!this.clientId || id !== this.clientId) {
                this.clientId = id;
                console.log("client Id: " + this.clientId);
                if (this.clientId) {
                    // Save locally for later
                    this.configurationService.saveString(Local.CLIENT_ID, this.clientId);
                } else {
                    // Try and get a client Id from local storage
                    this.configurationService.getString(Local.CLIENT_ID).then((idLocal: string) => {
                        this.clientId = idLocal;
                        console.log("client Id from local: " + this.clientId);
                    });
                }
            }

        });
    }

    private trackRoute(path: string): void {
        if (AnalyticsService.disabled) {
            return;
        }

        this.currentPage = path;
        gtag("config", AnalyticsService.GA_TRACKING_ID, {
            "page_path": path,
            "custom_map": {
                "dimension1": "version",
                "dimension2": "customer"
            },
            "version": this.wboVersion,
            "customer": this.clientId
        });
    }

    /**
     * Sends an event that occurred
     * @param {string} event Name of the event
     * @param label Optional. Either a DataResourceModel or a string that can provide more context to the event
     */
    public trackEvent(event: AnalyticEvent, label?: DataResourceModel<any> | AbstractSearchModel | string): void {
        if (AnalyticsService.disabled) {
            return;
        }

        let labelStr;
        if (label) {
            if (label instanceof DataResourceModel) {
                labelStr = label.getResource();
            } else if (label instanceof AbstractSearchModel) {
                labelStr = label.getResource();
            } else {
                labelStr = label;
            }
        }

        if (event.addPageToLabel) {
            if (!labelStr) {
                labelStr = "";
            }


            labelStr = this.currentPage + "/" + labelStr;
        }

        if (event.dialogAction) {
            this.checkStackSize();
        }

        gtag("event", event.action, {
            "event_category": event.category,
            "event_label": labelStr,
            "version": this.wboVersion,
            "customer": this.clientId
        });

        if (event.type === AnalyticType.CUSTOMER_MESSAGING_DETAILED) {
            let eventStr = event.category + "/" + event.action;
            if (label) {
                if (label instanceof DataResourceModel) {
                    eventStr = eventStr + "/" + label.getResource();
                } else if (label instanceof SearchModel) {
                    eventStr = eventStr + "/" + label.getResource();
                } else {
                    eventStr = eventStr + "/" + label;
                }
            }
            this.customerSupportMessageService.trackEvent(eventStr);

        } else if (event.type === AnalyticType.CUSTOMER_MESSAGING_SUMMARY) {
            this.customerSupportMessageService.trackEvent(event.category + "/" + event.action);
        }
    }

    /**
     * Tracks a timing
     * @param {AnalyticEvent} event Type of event
     * @param {number} value milliseconds
     * @param {DataResourceModel<any> | SearchModel<any> | string} label Additional info
     */
    public trackTiming(event: AnalyticEvent, value: number, label?: DataResourceModel<any> | SearchModel<any> | string): void {
        if (AnalyticsService.disabled) {
            return;
        }

        let labelStr = "";
        if (label) {
            if (label instanceof DataResourceModel) {
                labelStr = label.getResource();
            } else if (label instanceof SearchModel) {
                labelStr = label.getResource();
            } else {
                labelStr = label;
            }
        }

        if (event.action) {
            labelStr = event.action + "/" + labelStr;
        }

        gtag("event", "timing_complete", {
            "name": labelStr,
            "event_category": event.category,
            "value": value,
            "version": this.wboVersion,
            "customer": this.clientId
        });
    }

    /**
     * Tracks an exception
     */
    public trackException(message: string): void {
        if (AnalyticsService.disabled) {
            return;
        }

        gtag("event", "exception", {
            "description": message,
            "fatal": false,
            "version": this.wboVersion,
            "customer": this.clientId
        });
    }

    private checkStackSize(): void {
        if (this.modalService.modals) {
            this.modalService.modals.forEach((o) => {
                if (o instanceof ComponentRef && o.instance instanceof FUIStackedPageModalComponent) {
                    const stack: FUIStackedPageModalComponent = o.instance;
                    if (stack && stack.views) {
                        switch (stack.views.length) {
                            case 2:
                                this.trackEvent(AnalyticEvent.ENTITY_DIALOG_TAB_3);
                                break;
                            case 4:
                                this.trackEvent(AnalyticEvent.ENTITY_DIALOG_TAB_5);
                                break;
                            case 9:
                                this.trackEvent(AnalyticEvent.ENTITY_DIALOG_TAB_10);
                                break;
                            case 14:
                                this.trackEvent(AnalyticEvent.ENTITY_DIALOG_TAB_15);
                                break;
                        }
                    }
                }
            });
        }
    }
}
